import React, { useState, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { authService } from '../../services/authService';
import './EmailVerification.css';

export const EmailVerificationBanner: React.FC = () => {
  const { currentUser } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Define callbacks before conditional returns
  const handleResendVerification = useCallback(async () => {
    try {
      setIsLoading(true);
      setMessage(null);

      const result = await authService.resendEmailVerification();

      if (result.success) {
        setMessage({
          type: 'success',
          text: 'Verification email sent! Check your inbox.'
        });
      } else {
        setMessage({
          type: 'error',
          text: result.error || 'Failed to send verification email. Please try again.'
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Something went wrong. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleRefreshAndVerify = useCallback(() => {
    try {
      // Reload the user's current session to refresh the emailVerified status
      if (currentUser && currentUser.reload) {
        currentUser.reload().then(() => {
          // Reload the page to refresh all state
          window.location.reload();
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Failed to refresh. Please reload the page manually.'
      });
    }
  }, [currentUser]);

  const handleDismiss = useCallback(() => {
    setDismissed(true);
  }, []);

  // Only show for email/password users who haven't verified their email
  if (!currentUser || currentUser.emailVerified || dismissed) {
    return null;
  }

  // Check if user is using password provider (not Google OAuth)
  const isPasswordUser = currentUser.providerData?.[0]?.providerId === 'password';
  if (!isPasswordUser) {
    return null;
  }

  return (
    <div className="email-verification-banner">
      <div className="email-verification-content">
        <div className="email-verification-icon">
          ✉️
        </div>
        <div className="email-verification-text">
          <h3 className="email-verification-title">
            Please verify your email address!
          </h3>
          <p className="email-verification-message">
            Check your inbox for a verification link from {currentUser.email}.
            This helps keep your account secure.
          </p>
        </div>

        {message && (
          <div className={`email-verification-message-box ${message.type}`}>
            {message.text}
          </div>
        )}

        <div className="email-verification-actions">
          <button
            className="email-verification-button resend-button"
            onClick={handleResendVerification}
            disabled={isLoading}
          >
            {isLoading ? 'Sending...' : '📧 Resend Verification Email'}
          </button>
          <button
            className="email-verification-button refresh-button"
            onClick={handleRefreshAndVerify}
            disabled={isLoading}
          >
            ✓ I've verified, refresh
          </button>
          <button
            className="email-verification-button dismiss-button"
            onClick={handleDismiss}
            aria-label="Dismiss verification banner"
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
};
